import React, { LegacyRef, PropsWithChildren, forwardRef, useEffect, useMemo, useRef } from 'react';

import { ClearSelectionIcon, InputRightIcon } from './select-components';
import SelectInput from './select-input';
import { SelectCoreProps } from './select.types';
import { MenuItem, Popover } from '../..';

const classes = {
  wrapper: 'inline-block relative',
  popover: {
    root: 'origin-top-right absolute z-150 rounded-md',
    base: 'flex-col max-h-60 overflow-y-auto rounded-md border-t border-t-gray-300 shadow-dark',
  },
};

// eslint-disable-next-line max-lines-per-function
const SelectCoreWithoutRef = <T extends MenuItem>(
  props: PropsWithChildren<SelectCoreProps<T>>,
  ref?: LegacyRef<HTMLDivElement>
) => {
  const {
    allowClear = true,
    children,
    id,
    onBlur,
    onClearSelection,
    onDropdownVisibleChange,
    onFocus: onFocusCallback,
    onMenuOpen,
    onSearch,
    open,
    popoverClassesBase,
    searchTerm,
    selectedItem,
    width,
    containerSize = 'medium',
    placeholder,
  } = props;

  const effectiveWidth = width?.trim() ? width : '100%';

  const inputRef = useRef<HTMLInputElement>(null);

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    onMenuOpen(true);
    onDropdownVisibleChange?.(true);
    onFocusCallback?.(e);
  };

  const onMenuOpenChange = (open: boolean) => {
    onMenuOpen(open);
    onSearch('');
    onBlur?.();
  };

  useEffect(() => onDropdownVisibleChange?.(open === true), [onDropdownVisibleChange, open]);

  const popoverWidth = useMemo(
    () => (effectiveWidth && !effectiveWidth.includes('%') ? effectiveWidth : `${inputRef.current?.clientWidth}px`),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [effectiveWidth, inputRef.current?.clientWidth]
  );

  return (
    <div ref={ref} className={classes.wrapper} style={{ width: effectiveWidth }}>
      <Popover
        autoPlacementOptions={{ allowedPlacements: ['top', 'bottom'] }}
        content={children}
        containerClassName="w-full"
        innerContentClassName={popoverClassesBase ?? classes.popover.base}
        key={`Pop-${id}`}
        offsetOptions={{ mainAxis: 0 }}
        onOpenChange={onMenuOpenChange}
        open={open}
        overlayClassName={classes.popover.root}
        overlayStyle={{ width: popoverWidth }}
        showArrow={false}
        trigger="external"
      >
        <SelectInput
          allowClear={allowClear}
          key={`SelectionInput-${id}`}
          inputId={id}
          placeholder={placeholder}
          value={searchTerm === '' ? selectedItem?.label : searchTerm}
          contentWidth={effectiveWidth}
          onClearSelection={onClearSelection}
          onFocus={onFocus}
          onSearch={onSearch}
          ref={inputRef}
          containerSize={containerSize}
        />
        <ClearSelectionIcon
          inputId={id}
          allowClear={allowClear}
          inputHasSelection={!!selectedItem || searchTerm !== ''}
          onClearSelection={onClearSelection}
          size={containerSize}
        />
        <InputRightIcon inputId={id} inputRef={inputRef} menuOpen={open} size={containerSize} />
      </Popover>
    </div>
  );
};

export const SelectCore = forwardRef(SelectCoreWithoutRef) as <T extends MenuItem>(
  props: PropsWithChildren<SelectCoreProps<T>> & { ref?: LegacyRef<HTMLDivElement> }
) => JSX.Element;

export default SelectCore;
